/**
 * The figure that a launcher tile shows under its description: what the place holds now. Only
 * the places whose state an operator reads at a glance have a figure.
 */

import type { ActivityCount, RecentActivitySnapshot } from '@/types/events';
import type { FeedHealthMessage } from '@/types/healthMessages';
import { HealthSeverity } from '@/types/healthMessages';
import type {
    FleetNumbers, HealthNumbers, LauncherInfo, LauncherNumbers, LauncherPlaceKey,
} from '@/types/panels';
import { LauncherInfoKind } from '@/types/panels';

/** `noun` for `count` of them: the plural adds an `s`, as every noun the launcher counts does. */
export function getCountedNoun(count: number, noun: string): string {
    return count === 1 ? noun : `${noun}s`;
}

export function countBySeverity(issues: FeedHealthMessage[]): HealthNumbers {
    const countIssues = (severity: HealthSeverity): number => issues
        .filter((issue) => issue.message.severity === severity).length;
    const errors = countIssues(HealthSeverity.Error);
    const warnings = countIssues(HealthSeverity.Warning);
    return { errors, warnings, notices: issues.length - errors - warnings };
}

function getFleetInfo(numbers: FleetNumbers, noun: string): LauncherInfo | null {
    return numbers.total === 0 ? null : { kind: LauncherInfoKind.Fleet, noun, ...numbers };
}

function getActivityInfo(
    value: ActivityCount, snapshot: RecentActivitySnapshot,
): LauncherInfo | null {
    if (snapshot.isLoading || snapshot.error !== null) {
        return null;
    }
    return { kind: LauncherInfoKind.Activity, ...value };
}

export function getLauncherInfo(
    id: LauncherPlaceKey, numbers: LauncherNumbers,
): LauncherInfo | null {
    switch (id) {
        case 'cameras_list:cameras':
            return getFleetInfo(numbers.cameras, 'camera');
        case 'servers':
            return getFleetInfo(numbers.servers, 'server');
        case 'health_monitor':
            // While the issues are stale, the tile cannot tell that all is clear.
            if (numbers.isHealthStale) {
                return null;
            }
            return { kind: LauncherInfoKind.Health, ...numbers.health };
        case 'ai_search:objects':
            return getActivityInfo(numbers.recentActivity.objects,
                numbers.recentActivity);
        case 'ai_search:events':
            return getActivityInfo(numbers.recentActivity.events,
                numbers.recentActivity);
        default:
            return null;
    }
}
